import { CommonModule } from '@angular/common';
import { Component, inject, input, OnInit, output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { WHButtonComponent } from '../buttons/w-h-button/w-h-button.component';
import { firstValueFrom } from 'rxjs';
import { AppDialogService } from 'src/app/app-dialogs/app-dialog.service';
import { uriValidator } from 'src/app/app-services/form.service';

@Component({
  selector: 'w-h-link',
  template: `
    <form [formGroup]="linkFormGroup" class="flex--r-fs gap--8">
      <mat-form-field class="flex-1">
        <mat-label>{{ 'node.model.name' | translate }}</mat-label>
        <input matInput formControlName="fileName" />
      </mat-form-field>
      <mat-form-field class="flex-1">
        <mat-label>{{ 'app.ui.link.label' | translate }}</mat-label>
        <input matInput formControlName="documentUrl" />

        @if (linkFormGroup.get('documentUrl').hasError('invalidUri')) {
          <mat-error>{{ 'error.invalidUrl' | translate }}</mat-error>
        }
      </mat-form-field>

      <div class="flex--r-c-r flex-1 gap--8" style="margin-top: 10px">
        <w-h-button
          [text]="'app.save.label' | translate"
          matIcon="save"
          type="stroked"
          color="accent"
          [disabled]="linkFormGroup.invalid"
          (clicked)="submitLink()"
        />
        <w-h-button
          [text]="'app.cancel.label' | translate"
          matIcon="close"
          type="stroked"
          color="warn"
          (clicked)="cancelEdit()"
        />
      </div>
    </form>
  `,
  styles: `
    :host {
      width: 100%;
    }
  `,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    WHButtonComponent,
  ],
})
export class WHLinkComponent implements OnInit {
  private readonly appDialogService = inject(AppDialogService);
  linkFormGroup = new FormGroup({
    fileName: new FormControl('', Validators.required),
    documentUrl: new FormControl('', [Validators.required, uriValidator()]),
    id: new FormControl(''),
    sortPosition: new FormControl(''),
  });

  initialValues = input<any>();
  onSubmit = output<any>();
  onCancel = output<boolean>();

  ngOnInit(): void {
    if (this.initialValues) {
      this.linkFormGroup.patchValue(this.initialValues());
    }
  }

  cancelEdit() {
    this.linkFormGroup.reset();
    this.onCancel.emit(true);
  }

  async submitLink() {
    try {
      const link = this.linkFormGroup.controls?.documentUrl?.value;

      const normalizedUrl = encodeURI(decodeURI(link));

      const confirm =
        normalizedUrl === link
          ? true
          : await firstValueFrom(
              this.appDialogService
                .openConfirmMatDialog({
                  header: 'app.dialog.link.header',
                  body: 'app.dialog.link.text',
                  submitAction: 'app.dialog.link.button',
                  secondarySubmit: {
                    translateKey: 'app.save.label',
                    color: 'accent',
                    icon: 'done',
                  },
                })
                .afterClosed(),
            );

      if (confirm === 'secondarySubmit') {
        const newLink = normalizedUrl.split('%')[0];
        this.linkFormGroup.controls?.documentUrl?.setValue(newLink);
        this.onSubmit.emit(this.linkFormGroup.value);
      } else if (confirm) {
        this.linkFormGroup.controls?.documentUrl?.setValue(normalizedUrl);
        this.onSubmit.emit(this.linkFormGroup.value);
      }

      this.cancelEdit();
    } catch (error) {
      console.error(error);
    }
  }
}
